import React, {useEffect, useState} from "react";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {Swiper, SwiperSlide} from "swiper/react";
import {Pagination} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import NumberCounter from "@/customComponents/uiComponents/global/NumberCounter.jsx";
import {tripService} from "@/services/tripService";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {storage, STORAGE_KEYS, COUNTER_TYPES} from "@/utils/storage";
import {useLuggageTypes} from "@/hooks/useSelectables";
import {arrowRightWhite, oversizeBag} from "@img/index.js";
import {ModalFooter} from "../Components/BookingModalShell.jsx";

export function PassengerLuggage({setStep, closeModal}) {
    const [recalculateError, setRecalculateError] = useState(false);
    const [passengerLuggageType, setPassengerLuggageType] = useState('');
    const [passengerLuggageTypeShortcut, setPassengerLuggageTypeShortcut] = useState('');
    const [passengerLuggageName, setPassengerLuggageName] = useState('');
    const [passengerLuggageWeight, setPassengerLuggageWeight] = useState('');
    const [passengerLuggageHeight, setPassengerLuggageHeight] = useState('');
    const [passengerLuggageWidth, setPassengerLuggageWidth] = useState('');
    const [passengerLuggageImage, setPassengerLuggageImage] = useState('');
    const {data: passengerLuggageTypeDb = []} = useLuggageTypes();
    const [luggageCounters, setLuggageCounters] = useState({});
    const [recalculate, setRecalculate] = useState(false);

    // Total across every Oversized type, for the badge on the Oversized tile — the same
    // "how many did I pick" cue the Standard tiles already carry. Those tiles read
    // `luggageCounters`, which this step owns because its own counter writes to it; the
    // oversized counts are written by the separate overSizeLuggage step, so they are
    // read back from storage on mount (returning here remounts this step) and kept live
    // by the counter's broadcast event for as long as both are mounted.
    const [oversizedTotal, setOversizedTotal] = useState(0);
    useEffect(() => {
        const readTotal = () => passengerLuggageTypeDb
            .filter(option => option.category === 'Oversized')
            .reduce((sum, option) => sum + storage.getCounter(COUNTER_TYPES.OVERSIZED_LUGGAGE, option.id), 0);

        setOversizedTotal(readTotal());

        const handler = (e) => {
            if (typeof e?.detail?.key === 'string'
                && e.detail.key.startsWith(`hopn-counter-${COUNTER_TYPES.OVERSIZED_LUGGAGE}-`)) {
                setOversizedTotal(readTotal());
            }
        };
        window.addEventListener('hopn-counter-update', handler);
        return () => window.removeEventListener('hopn-counter-update', handler);
    }, [passengerLuggageTypeDb]);


    // Once the luggage list is available, seed the counter map from the saved
    // counters and select the default Standard item to display.
    useEffect(() => {
        if (passengerLuggageTypeDb.length === 0) return;

        const counters = {};
        passengerLuggageTypeDb.forEach(type => {
            const key = `hopn-counter-standardLuggage-${type.id}`;
            counters[key] = storage.getCounter(COUNTER_TYPES.STANDARD_LUGGAGE, type.id);
        });
        setLuggageCounters(counters);

        const defaultItem = passengerLuggageTypeDb.findLast(item => item.category === 'Standard') || passengerLuggageTypeDb[0];
        setPassengerLuggageType(defaultItem.id);
        setPassengerLuggageTypeShortcut(defaultItem.label.match(/\(([^)]+)\)/)?.[1] || defaultItem.short_label);
        setPassengerLuggageWeight(defaultItem.dimensions.weight);
        setPassengerLuggageHeight(defaultItem.dimensions.height);
        setPassengerLuggageWidth(defaultItem.dimensions.width);
        setPassengerLuggageImage(defaultItem.image_url);
        setPassengerLuggageName(defaultItem.label);
    }, [passengerLuggageTypeDb]);


    return (
        <>
            <ModalHeader
                title="Add Luggage"
                extraClasses=""
                totalSteps={2}
                cancelButton={false}
                cancelButtonType="text"
                backStep="passengerQuantity"
                showBackButton={true}
                setStep={setStep}
                closeModal={closeModal}
            />
            <div className="little-title modalPages-spacing">
                <h2>Add Luggage</h2>
            </div>
            <div className="hopn-baggage-display modalPages-spacing">
                <span className="hopn-badge hopn-size-l-badge">{passengerLuggageTypeShortcut}</span>
                <div className="hopn-suitcase-visual">
                    <div className="hopn-suitcase-visual-inner">
                        <div className="hopn-dimension hopn-height-dim">
                            <span className="hopn-dim-value">{passengerLuggageHeight} cm</span>
                        </div>
                        <div className="hopn-dimension hopn-weight-dim">
                            <span className="hopn-dim-value">{passengerLuggageWeight} kg</span>
                        </div>
                        <img decoding="async"
                             src={passengerLuggageImage}
                             alt={passengerLuggageName}/>
                        <div className="hopn-dimension hopn-width-dim">
                            <span className="hopn-dim-value">{passengerLuggageWidth} cm</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="hopn-quantity-selector">
                <NumberCounter
                    key={passengerLuggageType}
                    id={`standardLuggage-${passengerLuggageType}`}
                    variant="booking"
                    onChange={(newValue) => {
                        storage.setCounter(COUNTER_TYPES.STANDARD_LUGGAGE, passengerLuggageType, newValue);
                        const key = `hopn-counter-standardLuggage-${passengerLuggageType}`;
                        setLuggageCounters(prev => ({...prev, [key]: newValue}));
                    }}
                />

            </div>

            <div className="hopn-luggage-type-selector modalPages-spacing">
                {passengerLuggageTypeDb.length === 0 ? (
                    <Loader/>
                ) : (
                    <>
                        <Swiper
                            modules={[Pagination]}
                            spaceBetween={10}
                            navigation
                            pagination={{clickable: true}}
                            style={{width: "100%"}}
                            breakpoints={{
                                0: {
                                    slidesPerView: "auto",
                                },
                                576: {
                                    slidesPerView: 4.5,
                                },
                                767: {
                                    slidesPerView: 5.5,
                                },
                            }}
                        >
                            {passengerLuggageTypeDb
                                .filter(option => option.category === 'Standard')
                                .reverse()
                                .map((option, index) => (
                                    <SwiperSlide key={index}>
                                        <button
                                            key={option.id}
                                            className={`hopn-type-wrapper hopn-type-option ${String.fromCharCode(97 + index)} ${option.id === passengerLuggageType ? 'active' : ''}`}
                                            onClick={() => {
                                                setPassengerLuggageType(option.id);
                                                setPassengerLuggageTypeShortcut(option.short_label);
                                                setPassengerLuggageWeight(option.dimensions.weight);
                                                setPassengerLuggageHeight(option.dimensions.height);
                                                setPassengerLuggageWidth(option.dimensions.width);
                                                setPassengerLuggageImage(option.image_url);
                                                setPassengerLuggageName(option.label);
                                            }}
                                        >
                                            <span className="hopn-icon">
                                                {luggageCounters[`hopn-counter-standardLuggage-${option.id}`] > 0
                                                    ? <div
                                                        className="tooltip">{luggageCounters[`hopn-counter-standardLuggage-${option.id}`]}</div>
                                                    : ''
                                                }
                                                <img
                                                    decoding="async"
                                                    src={option.icon_url}
                                                    alt={`${option.label}`}
                                                />
                                            </span>
                                            <span className="hopn-label">
                                                {option.label.match(/\(([^)]+)\)/)?.[1] || option.short_label}
                                            </span>
                                        </button>
                                    </SwiperSlide>
                                ))}
                        </Swiper>

                        <div className="overSizedCategory">
                            <button
                                type="button"
                                className="hopn-type-wrapper"
                                onClick={() => setStep('overSizeLuggage')}
                            >
                                <label className="hopn-type-option hopn-type-oversized">
                                    <span className="hopn-icon">
                                        {oversizedTotal > 0
                                            ? <div className="tooltip">{oversizedTotal}</div>
                                            : ''
                                        }
                                        <img
                                            decoding="async"
                                            src={oversizeBag}
                                            alt={passengerLuggageName}
                                        />
                                    </span>
                                    <span className="hopn-label">Oversized</span>
                                </label>
                            </button>
                        </div>
                    </>
                )}
            </div>


            <ModalFooter>
                {
                    storage.get(STORAGE_KEYS.EDIT_STEP) === true
                        ? (
                            <>
                                {recalculateError !== false && (<div className="error-message">{recalculateError}</div>)}
                                <button
                                    className="hopn-add-passenger-button no-icon"
                                    disabled={recalculate}
                                    onClick={async () => {
                                        if (!tripService.needsRecalculation()) {
                                            setStep('orderDetails');
                                            return;
                                        }
                                        setRecalculate(true)
                                        const res = await tripService.recalculateCost();
                                        setRecalculate(false)
                                        if (res.success) {
                                            setStep('orderDetails');
                                        } else {
                                            console.error(res);
                                            setRecalculateError(res.error)
                                        }
                                    }}
                                >
                                    {recalculate ? <Loader color="#fff"/> : "Done"}
                                </button>
                            </>
                        )
                        : (
                            <button
                                className="hopn-add-passenger-button calendar"
                                onClick={() => setStep('travelingDateTime')}
                                disabled={!(passengerLuggageTypeDb.length)}
                            >
                                {passengerLuggageTypeDb.length === 0 ?
                                    <Loader color="#fff"/> : "+Date & Time"}
                                <img src={arrowRightWhite} alt="Arrow right"/>
                            </button>
                        )
                }

            </ModalFooter>
        </>
    )
}

export default PassengerLuggage;
